// FÜL-SÁV (tab strip) — nézet-váltás egy halvány, keretezett sávban: az aktív fül „kiemelkedik" (felület-színű
// lap + finom árnyék), és a felirat mellett DARABSZÁM-chip állhat.
//
// MIÉRT HARMADIK PRIMITÍV (és nem a `Segmented` vagy a `Tabs` átszabása) — a három szerepe MÁS:
//
//  • `ui/tabs.tsx` (Radix, ALÁHÚZOTT) — a BELSŐ felület fő-nézet-váltója (jegyek, üzenetek, eszközök). Ott a
//    tab-sor a lap tetején, teljes szélességű alapvonalon fut; ez a bevált minta a sűrű munka-felületen.
//  • `ui/segmented.tsx` (`role="radiogroup"`) — ŰRLAP-választó: kölcsönösen kizáró ÉRTÉK (típus, címzés).
//    A képernyőolvasó itt „választó-csoportot" mond, ami mezőnél helyes — nézet-váltásnál viszont félrevezető.
//  • ez a fájl (`role="tablist"`) — a PARTNER-FELÜLET fül-sávja, a terv szerinti geometriával
//    (`docs/foundations/design.md` §9.3 (a fül-sáv geometriája)): 3px konténer-padding, 1px keret,
//    `secondary` háttér, 12.5px felirat, 7px rádiusz, és a chip aktív állapotban brand-tónusú.
//
// A user épp ezt a különbséget mérte: a partner-lapokon a `Segmented` (14px felirat, keret
// nélkül) „nem egészen olyan, mint a design tervben". A geometria itt nem kozmetika — a fül-sáv a lap
// legfelső vezérlője, és ha az elcsúszik, az egész lap idegennek látszik.
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export interface TabStripItem {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Darabszám-chip a felirat után; `undefined` → nincs chip (a `0` MEGJELENIK — az is információ). */
  count?: number;
}

export interface TabStripProps {
  value: string;
  onChange: (value: string) => void;
  items: readonly TabStripItem[];
  /** A fül-sáv képernyőolvasó-neve (pl. a lista neve). */
  'aria-label': string;
  className?: string | undefined;
}

export function TabStrip({
  value,
  onChange,
  items,
  'aria-label': ariaLabel,
  className,
}: TabStripProps): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        // A VÁLYÚ (trough) SEMLEGES és a lap-háttérnél sötétebb, az aktív fül pedig `card`-ra emelkedik ki —
        // így a sáv MINDKÉT témában olvasható. Korábban `bg-secondary` volt: az light-ban `accent-100`, azaz
        // halvány ZÖLD felület (a zöld itt jelentést hordozna, nem hátteret), dark-ban pedig `44 44 48`, ami a
        // `--background`-tól (43 43 46) egy hajszálra van → a keret gyakorlatilag eltűnt. User-mérés.
        // A `bg-sunken` ugyanaz a mélyített felület, amit a rendszer máshol is használ (üres-állapot ikon,
        // sidebar) — nem új token.
        'border-border bg-sunken flex w-fit max-w-full flex-wrap gap-0.5 rounded-[9px] border p-[3px]',
        className,
      )}
    >
      {items.map((it) => {
        const active = value === it.value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(it.value)}
            className={cn(
              // Az IKON-MÉRET a komponensben dől el (`size-3.5` = 14px a 12.5px felirathoz), hogy a hívók
              // ne ismételjék, és a sáv ne tudjon lapról lapra elcsúszni — ugyanaz a szerep, mint a belső
              // felület `TabsTrigger`-ének svg-szabálya (user-kérés: „legyenek ott a tab elején
              // az ikonok, akárcsak admin oldalon").
              'ease-atlas inline-flex items-center gap-1.5 rounded-[7px] px-[11px] py-[5px] text-[12.5px] font-medium transition-colors duration-150',
              '[&_svg]:size-3.5 [&_svg]:shrink-0',
              'focus-visible:ring-accent-600 focus-visible:outline-none focus-visible:ring-2',
              active
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {it.icon}
            <span>{it.label}</span>
            {it.count !== undefined && (
              <span
                className={cn(
                  'rounded-full px-[5px] py-px text-[10.5px] font-semibold leading-[1.5]',
                  // Az aktív chip brand-tónusú (a terv szerint). Világos témán a `brand` felirat a
                  // `brand-soft` alapon halk, ezért ott a sötétebb árnyalat megy — ugyanaz a döntés, mint a
                  // `Segmented` chipjén.
                  active
                    ? 'bg-brand-soft text-brand-dark dark:text-brand'
                    : 'bg-card text-muted-foreground',
                )}
              >
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
